import { CircleCheck, Paperclip, Send, TriangleAlert, X } from 'lucide-react'
import {
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'

import { cn, HelpHint, Tooltip } from '@aeris/ui'

import { imageFileOf } from '../lib/clipboard-image.ts'
import { formatRemaining } from '../lib/inbox.ts'
import { fillQuickReply, type QuickReply } from '../lib/quick-replies.ts'

/**
 * O campo de resposta, e a linha que diz se dá para usá-lo — F085.
 *
 * ## O que a faixa de 24h virou
 *
 * Era um bloco de duas linhas com borda, fundo colorido e um parágrafo
 * explicando a regra da Meta — de ponta a ponta da conversa, permanente, acima
 * do campo de texto. Numa tela que fica aberta o dia inteiro, isso é uma
 * explicação que a pessoa lê uma vez e depois contorna com os olhos para
 * sempre.
 *
 * Virou uma linha. Aberta, ela é discreta: um ponto verde e o tempo que falta.
 * Perto do fim, âmbar. **Fechada, aí sim ela pesa** — porque aí a informação é
 * acionável: o campo de texto sumiu e é preciso escolher um modelo.
 *
 * A explicação por extenso continua acessível, no `HelpHint` — quem precisa
 * dela busca uma vez; quem não precisa não paga por ela em cada tela.
 *
 * ## O campo
 *
 * Cresce com o texto, de uma linha até ~140px, e depois rola por dentro. Um
 * `rows={2}` fixo (o que havia antes) é grande demais para "ok" e pequeno
 * demais para um endereço de entrega.
 *
 * A dica "Enter envia. Shift+Enter quebra a linha" saiu da tela e virou
 * tooltip do botão. Ela é verdadeira e útil UMA vez; ocupando uma linha
 * permanente, ela é só ruído — o pedido do dono foi exatamente esse.
 */

/** Menos de uma hora restante: a janela vira um aviso, não um enfeite. */
const CLOSING_SOON_MS = 60 * 60 * 1000

const WINDOW_HELP = (
  <HelpHint label="janela de 24 horas">
    A Meta só deixa responder livremente até 24h depois da ÚLTIMA mensagem que o
    CLIENTE mandou — não da última que o Aeris enviou. Passado esse prazo, o
    campo de texto livre desaparece e só um modelo aprovado pela Meta pode ser
    enviado, porque mandar fora da janela sem um modelo é o gateway quem recusa,
    não o Aeris.
  </HelpHint>
)

export const ServiceWindowLine = ({
  state,
  now,
}: {
  state: { open: boolean; expiresAt: Date | null }
  now: Date
}): ReactNode => {
  if (state.open && state.expiresAt !== null) {
    const closingSoon =
      state.expiresAt.getTime() - now.getTime() < CLOSING_SOON_MS

    return (
      <p
        className={cn(
          'flex items-center gap-1.5 px-3 py-1 text-(length:--text-xs)',
          closingSoon ? 'text-(--warning)' : 'text-(--text-muted)'
        )}
      >
        {closingSoon ? (
          <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />
        ) : (
          <CircleCheck
            aria-hidden="true"
            className="h-3.5 w-3.5 text-(--success)"
          />
        )}
        Janela aberta — faltam {formatRemaining(now, state.expiresAt)}
        {WINDOW_HELP}
      </p>
    )
  }

  return (
    <p className="flex items-start gap-2 border-t border-(--warning)/40 bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)">
      <TriangleAlert
        aria-hidden="true"
        className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
      />
      <span>
        Janela de 24 horas fechada — só um modelo aprovado pela Meta pode ser
        enviado. Confira a prévia antes: cada envio é cobrado.
      </span>
      {WINDOW_HELP}
    </p>
  )
}

const MIN_HEIGHT_PX = 40
const MAX_HEIGHT_PX = 140

/** O campo de formulário mais longo que o servidor aceita (`app.ts`). */
const CAPTION_MAX_BYTES = 1024

/** A imagem esperando para sair (03/10), com a prévia já montada. */
export type ComposerAttachment = {
  readonly file: File
  readonly previewUrl: string
}

export type MessageComposerProps = {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  disabled: boolean
  sending: boolean
  /**
   * A imagem anexada (03/10). Sem `onAttach`, o número não manda imagem (a
   * API oficial) e o clipe nem aparece; colar uma imagem cola nada.
   */
  attachment?: ComposerAttachment | null
  onAttach?: (file: File) => void
  onRemoveAttachment?: () => void
  /**
   * Os atalhos (F297): "/" no começo do campo lista os da vendedora, e
   * escolher um põe o texto no campo — com `{nome}` já trocado — para ela
   * revisar e mandar.
   */
  quickReplies?:
    | {
        readonly items: readonly QuickReply[]
        readonly customerName: string | null
      }
    | undefined
}

const foldTitle = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')

export const MessageComposer = ({
  value,
  onChange,
  onSubmit,
  disabled,
  sending,
  attachment = null,
  onAttach,
  onRemoveAttachment,
  quickReplies,
}: MessageComposerProps): ReactNode => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const slash = /^\/([^\n]*)$/.exec(value)
  const suggestions =
    slash === null || quickReplies === undefined
      ? []
      : quickReplies.items.filter((item) =>
          foldTitle(item.title).includes(foldTitle(slash[1] ?? '').trim())
        )
  const pick = (item: QuickReply): void => {
    onChange(fillQuickReply(item.text, quickReplies?.customerName ?? null))
  }

  /*
   * O print colado (03/10): Ctrl+V com uma imagem na área de transferência
   * anexa a imagem; com texto, cola o texto como sempre.
   */
  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    if (onAttach === undefined) return
    /*
     * Excel e Word põem, junto do texto copiado, uma FIGURA das células. Com
     * texto na área de transferência, quem colou quer o texto — auditoria de
     * 04/10. O print da tela não traz texto, e continua virando anexo.
     */
    if (event.clipboardData.getData('text/plain').trim() !== '') return
    const file = imageFileOf(
      event.clipboardData.items,
      event.clipboardData.files
    )
    if (file === null) return
    event.preventDefault()
    onAttach(file)
  }

  /*
   * A altura é recalculada a cada mudança de valor, e não só ao digitar: o
   * envio limpa o rascunho por fora (a mutação zera o estado), e sem isto o
   * campo ficaria alto e vazio depois de mandar um texto longo.
   *
   * `height = 'auto'` antes de ler `scrollHeight` não é supersticioso — sem
   * ele, `scrollHeight` nunca DIMINUI, porque o elemento já está esticado.
   */
  useEffect(() => {
    const node = textareaRef.current
    if (node === null) return
    node.style.height = 'auto'
    node.style.height = `${String(Math.min(Math.max(node.scrollHeight, MIN_HEIGHT_PX), MAX_HEIGHT_PX))}px`
  }, [value])

  // Com imagem, o texto é a legenda e pode ficar vazio.
  const empty = value.trim().length === 0 && attachment === null
  /*
   * A legenda vai num campo do formulário, e o servidor não aceita campo de
   * mais de 1024 bytes (`app.ts`): acento e emoji contam dobrado. Melhor dizer
   * aqui do que mandar e ver recusar — auditoria de 04/10.
   */
  const captionTooLong =
    attachment !== null &&
    new TextEncoder().encode(value.trim()).length > CAPTION_MAX_BYTES
  const blocked = empty || captionTooLong

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      // Com a lista de atalhos aberta, Enter escolhe o primeiro (F297).
      const first = suggestions[0]
      if (first !== undefined) {
        pick(first)
        return
      }
      if (!disabled && !blocked) onSubmit()
    }
  }

  return (
    <form
      className="flex flex-col gap-2 border-t border-(--border-default) bg-(--surface) px-3 py-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (!disabled && !blocked) onSubmit()
      }}
    >
      {attachment === null ? null : (
        <div className="flex items-center gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) p-2">
          <img
            src={attachment.previewUrl}
            alt="Imagem a enviar"
            className="h-16 w-16 shrink-0 rounded-(--radius-sm) object-cover"
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
              {attachment.file.name === ''
                ? 'Imagem colada'
                : attachment.file.name}
            </span>
            {captionTooLong ? (
              <span
                role="alert"
                className="text-(length:--text-xs) text-(--danger)"
              >
                Legenda longa demais para ir com a imagem. Mande a imagem com
                uma legenda curta e o texto numa mensagem separada.
              </span>
            ) : (
              <span className="text-(length:--text-xs) text-(--text-muted)">
                O texto abaixo vai como legenda
              </span>
            )}
          </span>
          <button
            type="button"
            aria-label="Tirar a imagem"
            disabled={sending}
            onClick={onRemoveAttachment}
            className="rounded-(--radius-sm) p-1 text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text-primary)"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      )}
      {suggestions.length === 0 ? null : (
        <ul
          aria-label="Atalhos"
          className="flex max-h-48 flex-col overflow-y-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface)"
        >
          {suggestions.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => {
                  pick(item)
                  textareaRef.current?.focus()
                }}
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-(--surface-hover)"
              >
                <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                  {item.title}
                </span>
                <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                  {fillQuickReply(
                    item.text,
                    quickReplies?.customerName ?? null
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        {onAttach === undefined ? null : (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              aria-label="Escolher imagem"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file !== undefined) onAttach(file)
                event.target.value = ''
              }}
            />
            <Tooltip
              side="top"
              content="Anexar imagem (ou cole um print com Ctrl+V)"
            >
              <button
                type="button"
                aria-label="Anexar imagem"
                disabled={disabled}
                onClick={() => {
                  fileRef.current?.click()
                }}
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-(--radius-lg)',
                  'text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary)',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                <Paperclip aria-hidden="true" className="h-4 w-4" />
              </button>
            </Tooltip>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
          rows={1}
          placeholder={
            attachment === null ? 'Digite uma mensagem…' : 'Legenda (opcional)…'
          }
          aria-label="Mensagem"
          className={cn(
            'min-h-10 flex-1 resize-none rounded-(--radius-lg) border border-(--border-default)',
            'bg-(--surface-sunken) px-3 py-2 text-(length:--text-sm) text-(--text-primary)',
            'placeholder:text-(--text-muted)',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)',
            'disabled:cursor-not-allowed disabled:opacity-60'
          )}
        />

        {/*
         * Botão quadrado com o rótulo no tooltip — o "Enviar" por extenso ao
         * lado do campo é largura gasta com uma palavra que o ícone já diz, numa
         * tela onde a largura é a queixa.
         */}
        <Tooltip
          side="top"
          content={
            <span>
              Enviar
              <br />
              Enter envia · Shift+Enter quebra a linha
            </span>
          }
        >
          <button
            type="submit"
            disabled={disabled || blocked || sending}
            aria-label="Enviar mensagem"
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-(--radius-lg)',
              'bg-(--brand-primary) text-(--text-on-brand)',
              'transition-colors duration-(--duration-fast) hover:bg-(--brand-primary-hover)',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            <Send aria-hidden="true" className="h-4 w-4" />
          </button>
        </Tooltip>
      </div>
    </form>
  )
}
